import Bugsnag from "@bugsnag/js";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { ApiErrorResponse } from "@Types/api/error";
import { LoginProvider, ScreenParams } from "@Types/login";
import { UserChecked } from "@Types/user";
import { getMetaContent } from "@Utils/meta";
import { convertToIdentifyProperties, setDataLayerTrack } from "@Utils/track";
import { convertUserCheckedFromApiToContent, convertUserFromApiToContent } from "@Utils/user";

const cheerzApiProvider = (locale: string): CheerzApiProvider => {
  const csrfToken = getMetaContent("csrf-token");

  if (!csrfToken) {
    throw new Error("CSRF Token not found");
  }

  return new CheerzApiProvider(locale, csrfToken);
};

export const getFacebookSdkLocale = (locale: string): string => {
  const sdkLocales: { [key: string]: string } = {
    en: "en_GB",
    es: "es_ES",
    fr: "fr_FR",
    it: "it_IT",
  };
  return sdkLocales[locale] || "en_US";
};

export const logWithProvider = (
  provider: LoginProvider,
  getProviderToken: () => Promise<string>,
  onChangeScreen: (newScreenParams: ScreenParams) => void,
  locale: string
): void => {
  const checkUser = (providerToken: string) => {
    return cheerzApiProvider(locale)
      .checkUser({ name: provider, token: providerToken })
      .then((response) => ({
        ...convertUserCheckedFromApiToContent(response, providerToken),
      }));
  };

  const redirection = (user: UserChecked): void => {
    if (user.isExisting && user.email && user.token) {
      cheerzApiProvider(locale)
        .omniauthClient(user.email, user.token, provider)
        .then((response) => {
          const userConnected = convertUserFromApiToContent(response);

          setDataLayerTrack("Identify", convertToIdentifyProperties(userConnected));
          onChangeScreen({
            action: "signIn",
            firstName: userConnected.firstName,
            screen: "welcome",
          });
        })
        .catch((error: ApiErrorResponse) => {
          Bugsnag.notify(
            Error(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on omniauthClient call"
            )
          );
        });
    } else {
      onChangeScreen({
        action: "signUp",
        provider,
        screen: "email",
        token: user.token,
      });
    }
  };

  getProviderToken()
    .then(checkUser)
    .then((response) => {
      redirection(response);
    })
    .catch((error: ApiErrorResponse) => {
      Bugsnag.notify(
        Error(
          error.response?.data?.error_description[0]
            ? `${error.response?.data?.error_description[0]}`
            : "Error on getProviderToken call"
        )
      );
    });
};
